/**
 * Dice rolling utilities
 */
/**
 * Parse a dice expression (e.g., "2d6+3", "1d20-1")
 */
export function parseDiceExpression(expression) {
    const match = expression.match(/^(\d+)d(\d+)(([+-]\d+)?)$/i);
    if (!match) {
        throw new Error(`Invalid dice expression: ${expression}`);
    }
    const count = parseInt(match[1], 10);
    const sides = parseInt(match[2], 10);
    const modifier = match[3] ? parseInt(match[3], 10) : 0;
    if (count < 1 || count > 100) {
        throw new Error('Dice count must be between 1 and 100');
    }
    if (![4, 6, 8, 10, 12, 20, 100].includes(sides)) {
        throw new Error('Invalid dice type. Supported: d4, d6, d8, d10, d12, d20, d100');
    }
    return { count, sides, modifier };
}
/**
 * Roll dice based on expression
 */
export function rollDice(expression, seed) {
    const { count, sides, modifier } = parseDiceExpression(expression);
    // If seed is provided, use seeded random (for deterministic rolls)
    const random = seed !== undefined ? seededRandom(seed) : Math.random;
    const rolls = [];
    let total = modifier;
    for (let i = 0; i < count; i++) {
        const roll = Math.floor(random() * sides) + 1;
        rolls.push([roll]);
        total += roll;
    }
    const breakdown = `${expression} = [${rolls.map(r => r[0]).join(', ')}]${modifier !== 0 ? ` ${modifier >= 0 ? '+' : ''}${modifier}` : ''} = ${total}`;
    return {
        expression,
        rolls,
        modifier,
        total,
        breakdown,
    };
}
/**
 * Seeded random number generator for deterministic rolls
 */
function seededRandom(seed) {
    let state = seed;
    return () => {
        // Mulberry32 PRNG
        state |= 0;
        state = (state + 0x6D2B79F5) | 0;
        let t = Math.imul(state ^ (state >>> 15), 1 | state);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
/**
 * Roll multiple dice expressions
 */
export function rollMultiple(expressions, seed) {
    return expressions.map(expr => rollDice(expr, seed));
}
/**
 * Validate a dice expression
 */
export function isValidDiceExpression(expression) {
    try {
        parseDiceExpression(expression);
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Get statistics for a dice expression (average, min, max)
 */
export function getDiceStats(expression) {
    const { count, sides, modifier } = parseDiceExpression(expression);
    return {
        min: count + modifier,
        max: count * sides + modifier,
        average: count * ((sides + 1) / 2) + modifier,
    };
}
//# sourceMappingURL=dice.js.map